// Vendor Payables — super_admin only. Shows what we owe each vendor for
// the orders we dispatched in a date range. Pulls per-line cost from
// vendor_product_pricing effective on each batch's dispatch_date.

import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { vendorPayablesApi, vendorApi, downloadAuthed } from '../api/services';
import InfoTip from '../components/InfoTip';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { Loader2, Download, AlertTriangle, IndianRupee } from 'lucide-react';

// Vendor pricing is INR-only (Venky 2026-05-14). USD kept in case any
// historical row carries it; unknown codes fall back to the prefix.
function fmtMoney(n, currency = 'INR') {
  if (n == null || isNaN(n)) return '—';
  const c = (currency || 'INR').toUpperCase();
  const sym = c === 'INR' ? '₹' : c === 'USD' ? '$' : `${c} `;
  // Indian digit grouping (₹23,99,411.93) so big totals read at a glance.
  return `${sym}${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function nDaysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

const PRESETS = [
  { key: 'thisMonth', label: 'This month', range: () => ({ from: `${todayISO().slice(0, 7)}-01`, to: todayISO() }) },
  { key: 'lastMonth', label: 'Last month', range: () => {
      const d = new Date(); d.setDate(1); d.setDate(0); // last day of previous month
      const iso = d.toISOString().slice(0, 10);
      return { from: `${iso.slice(0, 7)}-01`, to: iso };
    } },
  { key: '7d',  label: 'Last 7 days',  range: () => ({ from: nDaysAgoISO(6),  to: todayISO() }) },
  { key: '30d', label: 'Last 30 days', range: () => ({ from: nDaysAgoISO(29), to: todayISO() }) },
];

export default function VendorPayables() {
  // Deep-link support: /admin/vendor-payables?vendorId=…&from=…&to=…
  // (used by Payment Review's "line-by-line statement" and the digest).
  const [searchParams] = useSearchParams();
  const qpFrom = searchParams.get('from');
  const qpTo = searchParams.get('to');
  const qpVendor = searchParams.get('vendorId');
  const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

  const [from, setFrom] = useState(DATE_RE.test(qpFrom || '') ? qpFrom : `${todayISO().slice(0, 7)}-01`);
  const [to, setTo] = useState(DATE_RE.test(qpTo || '') ? qpTo : todayISO());
  const [preset, setPreset] = useState(qpFrom ? 'custom' : 'thisMonth');
  const [vendorId, setVendorId] = useState(qpVendor || '');
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    vendorApi.list().then((r) => setVendors(r.data || [])).catch(() => {});
  }, []);

  async function run(f = from, t = to) {
    setLoading(true);
    setData(null);
    try {
      const res = await vendorPayablesApi.build({ from: f, to: t, ...(vendorId ? { vendorId } : {}) });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to build report');
    } finally { setLoading(false); }
  }

  function applyPreset(p) {
    const { from: f, to: t } = p.range();
    setPreset(p.key); setFrom(f); setTo(t);
    run(f, t);
  }

  // Auto-run on mount with the default date range.
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  function downloadCsv() {
    const qs = new URLSearchParams({ from, to, ...(vendorId ? { vendorId } : {}) }).toString();
    downloadAuthed(
      `/reports/vendor-payables/export.csv?${qs}`,
      `VENDOR-PAYABLES-${from}_to_${to}.csv`
    ).catch((err) => toast.error(err.response?.data?.error || 'Download failed'));
  }

  // Shipping is charged per parcel, not per product line — so the detail
  // view groups lines package-wise: one shaded header per parcel carrying
  // the destination, shipper and shipping cost, then its product lines.
  const detailGroupedByVendor = useMemo(() => {
    if (!data?.rows) return [];
    const map = new Map();
    for (const r of data.rows) {
      if (!map.has(r.vendor_id)) map.set(r.vendor_id, { vendor_name: r.vendor_name, parcels: new Map() });
      const v = map.get(r.vendor_id);
      if (!v.parcels.has(r.shipment_id)) {
        v.parcels.set(r.shipment_id, {
          shipment_id: r.shipment_id,
          tracking_number: r.tracking_number,
          batch_number: r.batch_number,
          dispatch_date: r.dispatch_date,
          order_id_external: r.order_id_external,
          customer_name: r.customer_name,
          shipping_country: r.shipping_country,
          shipping_zone: r.shipping_zone,
          shipper: r.shipper,
          shipping_rate: r.shipment_shipping_rate,
          gross_shipping_rate: r.gross_shipping_rate,
          is_reship: r.is_reship,
          reship_reason: r.reship_reason,
          applied_product_pct: r.applied_product_pct,
          applied_shipping_pct: r.applied_shipping_pct,
          rows: [],
        });
      }
      v.parcels.get(r.shipment_id).rows.push(r);
    }
    return Array.from(map.values()).map((v) => ({
      vendor_name: v.vendor_name,
      parcels: Array.from(v.parcels.values()),
    }));
  }, [data]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <IndianRupee className="w-5 h-5 text-emerald-600" />
        <h1 className="text-2xl font-semibold text-gray-900">Vendor Payables</h1>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        What we owe each vendor for orders dispatched in the date range.{' '}
        <InfoTip title="How payables are calculated" align="right">
          For every product line dispatched in the range: quantity × the
          vendor's INR unit price that was <b>in effect on the dispatch
          date</b> (so a later price change never alters an older bill).
          Shipping is charged <b>per parcel by destination</b> — the rate
          comes from the vendor's shipping rate card (Country + State, see
          the Vendors page); parcels with no matching destination use the
          vendor's flat per-package rate. <b>Reship orders</b> bill at the
          vendor's reship policy (e.g. 50% product + 50% shipping when the
          parcel was lost or damaged; full price for second reships and
          Rybelsus) — see the Reship policy panel on the Vendors page.
          Billing is settled at month end.
          Lines marked <b>unpriced</b> have no price on file for that
          product — they are counted in quantities but add ₹0, so the total
          is understated until Kalpana prices them on the Vendors page.
        </InfoTip>
      </p>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {PRESETS.map((p) => (
            <button key={p.key} onClick={() => applyPreset(p)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
                      preset === p.key
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
          <label className="block">
            <span className="block text-xs uppercase tracking-wide text-gray-500 mb-1">From</span>
            <input type="date" value={from}
                   onChange={(e) => { setFrom(e.target.value); setPreset('custom'); }}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-wide text-gray-500 mb-1">To</span>
            <input type="date" value={to}
                   onChange={(e) => { setTo(e.target.value); setPreset('custom'); }}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-wide text-gray-500 mb-1">Vendor</span>
            <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="">All vendors</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </label>
          <button onClick={() => run()} disabled={loading}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate'}
          </button>
          <button onClick={downloadCsv}
                  className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Building report…
        </div>
      )}

      {data && !loading && (
        <>
          {data.total.needs_review_lines > 0 && (
            <div className="mb-4 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-900">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5" />
                <div>
                  <strong>{data.total.needs_review_lines}</strong> reship line(s) need a
                  price decision from the vendor ("wrong product" reships — the vendor
                  decides the product charge case by case). They add ₹0 until agreed —
                  check the line detail and confirm with the vendor.
                </div>
              </div>
            </div>
          )}
          {data.total.unpriced_lines > 0 && (
            <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5" />
                <div>
                  <strong>{data.total.unpriced_lines}</strong> line(s) totalling{' '}
                  <strong>{data.total.unpriced_qty}</strong> unit(s) are unpriced — the
                  vendor catalog has no matching entry. Add them on Vendors → vendor → Import prices,
                  then re-run.
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold">
              Per-vendor summary  ·  Total due: {fmtMoney(data.total.total_due)}
              <span className="ml-2 text-xs font-normal text-gray-600">
                = products {fmtMoney(data.total.priced_line_cost)} + shipping {fmtMoney(data.total.shipping_cost)}
              </span>{' '}
              <InfoTip title="Reading this table" align="right">
                <b>Line cost</b> = quantity × unit price for every priced
                product line. <b>Shipping</b> = the vendor's per-shipment
                charge. <b>Total due</b> = line cost + shipping.
                <b> Unpriced</b> shows lines that add ₹0 because no price is
                on file — fix those before treating the total as final.
                "Show line-item detail" below lists every order line behind
                these numbers.
              </InfoTip>
              <span className="ml-3 text-xs text-gray-500 font-normal">
                {data.total.batches} batch(es) · {data.total.orders} order(s) · {data.total.shipments} shipment(s) · {data.total.items} line(s)
              </span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2">Vendor</th>
                  <th className="text-right px-3 py-2">Batches</th>
                  <th className="text-right px-3 py-2">Orders</th>
                  <th className="text-right px-3 py-2">Items</th>
                  <th className="text-right px-3 py-2">Line cost</th>
                  <th className="text-right px-3 py-2">Shipping</th>
                  <th className="text-right px-3 py-2">Reship</th>
                  <th className="text-right px-3 py-2">Total due</th>
                  <th className="text-right px-3 py-2">Unpriced</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.byVendor.map((v) => (
                  <tr key={v.vendor_id}>
                    <td className="px-3 py-2 font-medium">{v.vendor_name}</td>
                    <td className="px-3 py-2 text-right">{v.batches}</td>
                    <td className="px-3 py-2 text-right">{v.orders}</td>
                    <td className="px-3 py-2 text-right">{v.items}</td>
                    <td className="px-3 py-2 text-right">{fmtMoney(v.priced_line_cost)}</td>
                    <td className="px-3 py-2 text-right">{fmtMoney(v.shipping_cost)}</td>
                    <td className="px-3 py-2 text-right text-xs text-violet-700">
                      {v.reship?.orders > 0 ? `${fmtMoney(v.reship.total_due)} · ${v.reship.orders} ord` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold">{fmtMoney(v.total_due)}</td>
                    <td className="px-3 py-2 text-right text-xs text-amber-700">
                      {v.unpriced_lines > 0 ? `${v.unpriced_lines} line · ${v.unpriced_qty} qty` : '—'}
                    </td>
                  </tr>
                ))}
                {data.byVendor.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-sm text-gray-500">
                    No shipments dispatched in this range.
                  </td></tr>
                )}
              </tbody>
              {data.byVendor.length > 0 && (
                <tfoot className="bg-gray-50 text-sm font-semibold">
                  <tr>
                    <td className="px-3 py-2">All vendors</td>
                    <td className="px-3 py-2 text-right">{data.total.batches}</td>
                    <td className="px-3 py-2 text-right">{data.total.orders}</td>
                    <td className="px-3 py-2 text-right">{data.total.items}</td>
                    <td className="px-3 py-2 text-right">{fmtMoney(data.total.priced_line_cost)}</td>
                    <td className="px-3 py-2 text-right">{fmtMoney(data.total.shipping_cost)}</td>
                    <td className="px-3 py-2 text-right text-xs text-violet-700">
                      {data.total.reship?.orders > 0 ? `${fmtMoney(data.total.reship.total_due)} · ${data.total.reship.orders} ord` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">{fmtMoney(data.total.total_due)}</td>
                    <td className="px-3 py-2 text-right text-xs text-amber-700">
                      {data.total.unpriced_lines > 0 ? `${data.total.unpriced_lines} line` : '—'}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {data.total.regular && (
            <div className="mb-4">
              <div className="flex items-center gap-1 mb-2">
                <h2 className="text-sm font-semibold text-gray-900">Regular vs reship</h2>
                <InfoTip title="Reading this breakdown" align="left">
                  Splits the bill into <b>regular orders</b> (first dispatch)
                  and <b>reships</b> (re-sent after loss, damage, wrong
                  product, etc.). Reships bill at the vendor's reship policy,
                  so the reship card also shows what those parcels would have
                  cost at <b>full price</b> — the difference is the discount
                  the policy gives us. The reasons table shows where reships
                  are coming from and what each reason is costing.
                </InfoTip>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Regular orders</div>
                  <div className="text-xl font-semibold text-gray-900">{fmtMoney(data.total.regular.total_due)}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {data.total.regular.orders} order(s) · {data.total.regular.shipments} parcel(s) · {data.total.regular.items} line(s)
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    products {fmtMoney(data.total.regular.product_cost)} + shipping {fmtMoney(data.total.regular.shipping_cost)}
                  </div>
                </div>
                <div className="bg-violet-50/60 rounded-xl border border-violet-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-violet-700 mb-1">Reship orders</div>
                  <div className="text-xl font-semibold text-gray-900">{fmtMoney(data.total.reship.total_due)}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {data.total.reship.orders} order(s) · {data.total.reship.shipments} parcel(s) · {data.total.reship.items} line(s)
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    products {fmtMoney(data.total.reship.product_cost)} + shipping {fmtMoney(data.total.reship.shipping_cost)}
                  </div>
                  {data.total.reship.orders > 0 && (
                    <div className="text-xs text-violet-700 mt-1">
                      full price would be {fmtMoney(data.total.reship.gross_value)} — reship policies save{' '}
                      <b>{fmtMoney(Math.max(0, data.total.reship.gross_value - data.total.reship.total_due))}</b>
                    </div>
                  )}
                </div>
              </div>

              {(data.reshipByReason || []).length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold">
                    Reships by reason{' '}
                    <InfoTip title="Reships by reason">
                      Why re-sends happened in this range and what each reason
                      costs. <b>Billed</b> = what the vendor's reship policy
                      charges us. <b>Full price</b> = the same parcels with no
                      discount. <b>Saved</b> = the difference. A red badge
                      means some lines bill ₹0 until the vendor agrees a
                      charge ("wrong product" reships).
                    </InfoTip>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="text-left px-3 py-2">Reason</th>
                        <th className="text-right px-3 py-2">Orders</th>
                        <th className="text-right px-3 py-2">Parcels</th>
                        <th className="text-right px-3 py-2">Products</th>
                        <th className="text-right px-3 py-2">Shipping</th>
                        <th className="text-right px-3 py-2">Billed</th>
                        <th className="text-right px-3 py-2">Full price</th>
                        <th className="text-right px-3 py-2">Saved</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.reshipByReason.map((a) => (
                        <tr key={a.reason}>
                          <td className="px-3 py-2 capitalize">
                            {a.reason === 'no_reason' ? <span className="text-gray-500 italic">no reason recorded</span> : a.reason.replace(/_/g, ' ')}
                            {a.needs_review_lines > 0 && (
                              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[10px] font-semibold">
                                {a.needs_review_lines} line(s) vendor decides — billed ₹0
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">{a.orders}</td>
                          <td className="px-3 py-2 text-right">{a.shipments}</td>
                          <td className="px-3 py-2 text-right">{fmtMoney(a.product_cost)}</td>
                          <td className="px-3 py-2 text-right">{fmtMoney(a.shipping_cost)}</td>
                          <td className="px-3 py-2 text-right font-semibold">{fmtMoney(a.total_due)}</td>
                          <td className="px-3 py-2 text-right text-gray-500">{fmtMoney(a.gross_value)}</td>
                          <td className="px-3 py-2 text-right text-violet-700">{fmtMoney(Math.max(0, a.gross_value - a.total_due))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between mb-2">
            <button onClick={() => setShowDetail((s) => !s)}
                    className="text-sm text-blue-600 hover:underline">
              {showDetail ? '▾ Hide line-item detail' : '▸ Show line-item detail'}
            </button>
            <span className="text-xs text-gray-500">{data.rows?.length || 0} line(s)</span>
          </div>

          {showDetail && detailGroupedByVendor.map((g) => (
            <div key={g.vendor_name} className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-3">
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold">
                {g.vendor_name}
                <span className="ml-2 text-xs text-gray-500 font-normal">{g.parcels.length} parcel(s)</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2">Product</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-right px-3 py-2">Unit price</th>
                    <th className="text-right px-3 py-2 w-32">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {g.parcels.map((p) => {
                    const parcelLines = p.rows.reduce((a, r) => a + r.line_cost, 0);
                    return (
                      <React.Fragment key={p.shipment_id}>
                        <tr className={`border-t ${p.is_reship ? 'bg-violet-50/70 border-violet-100' : 'bg-blue-50/60 border-blue-100'}`}>
                          <td colSpan={3} className="px-3 py-2 text-xs">
                            <span className="font-mono font-semibold text-gray-900">{p.order_id_external}</span>
                            {p.is_reship && (
                              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded bg-violet-600 text-white text-[10px] font-semibold uppercase">
                                Reship{p.reship_reason ? ` · ${p.reship_reason.replace(/_/g, ' ')}` : ''}
                                {p.applied_product_pct != null && ` · product ${p.applied_product_pct}%`}
                                {p.applied_product_pct == null && ' · product: vendor decides'}
                              </span>
                            )}
                            <span className="text-gray-600"> · {p.customer_name}</span>
                            <span className="text-gray-600"> · {p.shipping_country}{p.shipping_zone && p.shipping_zone !== '-' ? ` / ${p.shipping_zone}` : ''}</span>
                            <span className="text-gray-500"> · {format(new Date(p.dispatch_date), 'dd MMM yyyy')} · batch {p.batch_number}</span>
                            {p.tracking_number && <span className="text-gray-600"> · <span className="font-mono">{p.tracking_number}</span></span>}
                          </td>
                          <td className="px-3 py-2 text-right text-xs whitespace-nowrap">
                            <span className="text-gray-600">Shipping ({p.shipper || 'flat rate'})</span>{' '}
                            <span className="font-semibold text-gray-900">{fmtMoney(p.shipping_rate)}</span>
                            {p.is_reship && p.applied_shipping_pct != null && p.applied_shipping_pct < 100 && (
                              <span className="text-violet-700"> ({p.applied_shipping_pct}% of {fmtMoney(p.gross_shipping_rate)})</span>
                            )}
                          </td>
                        </tr>
                        {p.rows.map((r, i) => (
                          <tr key={i} className={`border-t border-gray-100 ${r.needs_review ? 'bg-rose-50/50' : r.unpriced ? 'bg-amber-50/40' : ''}`}>
                            <td className="px-3 py-1.5 text-xs pl-6">{r.product_name}</td>
                            <td className="px-3 py-1.5 text-right text-xs">{r.quantity}</td>
                            <td className="px-3 py-1.5 text-right text-xs">
                              {r.needs_review
                                ? <span className="text-rose-700 font-medium">vendor decides</span>
                                : r.unpriced ? <span className="text-amber-700">unpriced</span> : fmtMoney(r.unit_price, r.currency)}
                            </td>
                            <td className="px-3 py-1.5 text-right text-xs">
                              {fmtMoney(r.line_cost, r.currency)}
                              {r.is_reship && !r.needs_review && r.applied_product_pct != null && r.applied_product_pct < 100 && (
                                <span className="text-violet-700"> ({r.applied_product_pct}% of {fmtMoney(r.gross_line_cost, r.currency)})</span>
                              )}
                            </td>
                          </tr>
                        ))}
                        <tr className="border-t border-gray-100">
                          <td colSpan={3} className="px-3 py-1.5 text-right text-[11px] text-gray-500 uppercase tracking-wide">
                            Parcel total (products + shipping)
                          </td>
                          <td className="px-3 py-1.5 text-right text-xs font-semibold">
                            {fmtMoney(parcelLines + p.shipping_rate)}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
